import React, { useState, useEffect } from 'react';
import { Dialog } from 'primereact/dialog';
import { InputTextarea } from 'primereact/inputtextarea';
import { Dropdown } from 'primereact/dropdown';
import { MultiSelect } from 'primereact/multiselect';
import { Calendar } from 'primereact/calendar';
import { Button } from 'primereact/button';
import { getClientsList, getBranches } from '../utilities/api';
import { isSuperAdmin, getOwnClientId } from './notificationScope';
import { STATUS_OPTIONS } from './notificationStatus';
import '../masters/CategoryMaster.css';
import './Notifications.css';

/** 'YYYY-MM-DD HH:mm:ss' from the API -> Date. The space form is not reliably parsed. */
const parseDateTime = (value) => {
    if (!value) return null;
    const date = new Date(String(value).replace(' ', 'T'));
    return isNaN(date.getTime()) ? null : date;
};

/** Date -> 'YYYY-MM-DD HH:mm:ss' in local time, so the stored value matches what was picked. */
const formatDateTime = (date) => {
    if (!date) return null;
    const pad = (n) => String(n).padStart(2, '0');
    return (
        `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ` +
        `${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`
    );
};

const NotificationsDialog = ({ visible, notificationData, onHide, onSave }) => {
    const [form, setForm]         = useState({});
    const [errors, setErrors]     = useState({});
    const [clients, setClients]   = useState([]);
    const [branches, setBranches] = useState([]);

    const superAdmin = isSuperAdmin();
    const isEdit     = !!notificationData?.notification_id;

    // Editing always targets the one existing row, so the multi-target pickers are
    // only offered when creating.
    const multiTarget = !isEdit;

    useEffect(() => {
        if (!visible) return;
        let cancelled = false;
        const ownClientId = getOwnClientId();
        getClientsList()
            .then((rows) => {
                if (cancelled) return;
                setClients(
                    rows
                        .filter((c) => superAdmin || String(c.client_id) === ownClientId)
                        .map((c) => ({ label: c.client_name, value: String(c.client_id) }))
                );
            })
            .catch(() => !cancelled && setClients([]));
        return () => { cancelled = true; };
    }, [visible, superAdmin]);

    useEffect(() => {
        if (!visible) return;
        setErrors({});
        if (notificationData) {
            setForm({
                notification_id:      notificationData.notification_id,
                notification_details: notificationData.notification_details || '',
                client_ids:           notificationData.client_id != null ? [String(notificationData.client_id)] : [],
                branch_ids:           notificationData.branch_id != null ? [String(notificationData.branch_id)] : [],
                expiry_date:          parseDateTime(notificationData.expiry_date),
                status:               notificationData.status || 'Active'
            });
        } else {
            setForm({
                notification_details: '',
                client_ids:           superAdmin ? [] : [getOwnClientId()].filter(Boolean),
                branch_ids:           [],
                expiry_date:          null,
                status:               'Active'
            });
        }
    }, [visible, notificationData, superAdmin]);

    // Branches only make sense for exactly one client — they belong to one client each,
    // so a multi-client send is always client-wide.
    const singleClientId = form.client_ids?.length === 1 ? form.client_ids[0] : null;

    useEffect(() => {
        if (!visible || !singleClientId) {
            setBranches([]);
            return undefined;
        }
        let cancelled = false;
        getBranches(singleClientId)
            .then((rows) => {
                if (cancelled) return;
                setBranches(rows.map((b) => ({ label: b.branch_name, value: String(b.branch_id) })));
            })
            .catch(() => !cancelled && setBranches([]));
        return () => { cancelled = true; };
    }, [visible, singleClientId]);

    const update = (field, val) => {
        setForm((prev) => ({ ...prev, [field]: val }));
        if (errors[field]) setErrors((prev) => { const e = { ...prev }; delete e[field]; return e; });
    };

    const handleClientsChange = (val) => {
        // Dropping to zero or back to many clients invalidates any branch picks.
        setForm((prev) => ({ ...prev, client_ids: val, branch_ids: val?.length === 1 ? prev.branch_ids : [] }));
        if (errors.client_ids) setErrors((prev) => { const e = { ...prev }; delete e.client_ids; return e; });
    };

    const validate = () => {
        const errs = {};
        if (!form.notification_details?.trim()) errs.notification_details = 'Notification details are required';
        if (!form.client_ids?.length) errs.client_ids = 'Select at least one client';
        if (form.expiry_date && form.expiry_date.getTime() < Date.now()) {
            errs.expiry_date = 'Expiry must be in the future';
        }
        setErrors(errs);
        return Object.keys(errs).length === 0;
    };

    const handleSave = () => {
        if (!validate()) return;
        const userId = localStorage.getItem('username') || localStorage.getItem('userid') || '';
        const base = {
            notification_details: form.notification_details.trim(),
            expiry_date:          formatDateTime(form.expiry_date),
            status:               form.status,
            created_by:           userId,
            updated_by:           userId,
            // Authoring is GSPOS-only; the server re-checks this id's role.
            acting_user_id:       localStorage.getItem('userid') || ''
        };

        if (isEdit) {
            onSave({
                ...base,
                notification_id: form.notification_id,
                client_id:       form.client_ids[0],
                branch_id:       form.branch_ids?.[0] || null
            });
            return;
        }

        onSave({
            ...base,
            notification_id: null,
            client_ids:      form.client_ids,
            // Empty means every branch of each selected client.
            branch_ids:      form.branch_ids?.length ? form.branch_ids : null
        });
    };

    /** Plain-English summary of how many rows this will create. */
    const targetSummary = () => {
        const clientCount = form.client_ids?.length || 0;
        if (!clientCount) return null;
        const branchCount = form.branch_ids?.length || 0;
        if (branchCount) return `Creates ${branchCount} notification${branchCount > 1 ? 's' : ''} — one per selected branch.`;
        if (clientCount > 1) return `Creates ${clientCount} notifications — one per client, reaching all their branches.`;
        return 'Creates 1 notification, reaching all branches of this client.';
    };

    const dialogHeader = (
        <div className="sub-dialog-header">
            <span className="sub-dialog-header-icon">
                <i className="pi pi-bell" />
            </span>
            <div>
                <div className="sub-dialog-header-text">
                    {isEdit ? 'Edit Notification' : 'Add Notification'}
                </div>
                <div style={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.75)', marginTop: 2 }}>
                    {isEdit ? 'Update the alert details below' : 'Send one alert to one or many clients'}
                </div>
            </div>
        </div>
    );

    const dialogFooter = (
        <div className="flex justify-content-end gap-2">
            <Button label="Cancel" icon="pi pi-times" className="sub-dialog-cancel" onClick={onHide} />
            <Button label={isEdit ? 'Update' : 'Save'} icon="pi pi-check" className="sub-dialog-save" onClick={handleSave} />
        </div>
    );

    return (
        <Dialog
            header={dialogHeader}
            visible={visible}
            style={{ width: '620px' }}
            breakpoints={{ '680px': '94vw' }}
            modal
            position="center"
            footer={dialogFooter}
            onHide={onHide}
            draggable={false}
            resizable={false}
            className="sub-master-dialog nf-dialog p-fluid"
        >
            <div className="sub-form-grid">

                {/* Notification Details — full width */}
                <div className="sub-field-group sub-field-full">
                    <label className="sub-field-label">
                        <i className="pi pi-comment sub-field-label-icon" />
                        Notification Details <span style={{ color: '#ef4444' }}>*</span>
                    </label>
                    <InputTextarea
                        value={form.notification_details || ''}
                        onChange={(e) => update('notification_details', e.target.value)}
                        rows={4}
                        autoResize
                        placeholder="e.g. Scheduled maintenance on Sunday 2 AM - 4 AM"
                        className={`w-full${errors.notification_details ? ' p-invalid' : ''}`}
                    />
                    {errors.notification_details && (
                        <span className="sub-error-msg">
                            <i className="pi pi-exclamation-circle" /> {errors.notification_details}
                        </span>
                    )}
                </div>

                {/* Clients — full width: selection chips need the room to wrap */}
                <div className="sub-field-group sub-field-full">
                    <label className="sub-field-label">
                        <i className="pi pi-briefcase sub-field-label-icon" />
                        {multiTarget ? 'Clients' : 'Client'} <span style={{ color: '#ef4444' }}>*</span>
                    </label>
                    {multiTarget ? (
                        <MultiSelect
                            value={form.client_ids || []}
                            options={clients}
                            onChange={(e) => handleClientsChange(e.value)}
                            placeholder="Select client(s)"
                            display="chip"
                            filter={superAdmin}
                            maxSelectedLabels={3}
                            selectedItemsLabel="{0} clients selected"
                            disabled={!superAdmin}
                            className={`w-full${errors.client_ids ? ' p-invalid' : ''}`}
                        />
                    ) : (
                        <Dropdown
                            value={form.client_ids?.[0] || null}
                            options={clients}
                            onChange={(e) => handleClientsChange(e.value ? [e.value] : [])}
                            placeholder="Select client"
                            filter={superAdmin}
                            disabled={!superAdmin}
                            className={`w-full${errors.client_ids ? ' p-invalid' : ''}`}
                        />
                    )}
                    {errors.client_ids && (
                        <span className="sub-error-msg">
                            <i className="pi pi-exclamation-circle" /> {errors.client_ids}
                        </span>
                    )}
                </div>

                {/* Branches — full width for the same reason */}
                <div className="sub-field-group sub-field-full">
                    <label className="sub-field-label">
                        <i className="pi pi-building sub-field-label-icon" />
                        {multiTarget ? 'Branches' : 'Branch'}
                    </label>
                    {multiTarget ? (
                        <MultiSelect
                            value={form.branch_ids || []}
                            options={branches}
                            onChange={(e) => update('branch_ids', e.value)}
                            placeholder={singleClientId ? 'All Branches' : 'Pick one client first'}
                            display="chip"
                            filter
                            maxSelectedLabels={3}
                            selectedItemsLabel="{0} branches selected"
                            disabled={!singleClientId}
                            className="w-full"
                        />
                    ) : (
                        <Dropdown
                            value={form.branch_ids?.[0] || null}
                            options={branches}
                            onChange={(e) => update('branch_ids', e.value ? [e.value] : [])}
                            placeholder="All Branches"
                            showClear
                            disabled={!singleClientId}
                            className="w-full"
                        />
                    )}
                </div>

                {/* Expiry */}
                <div className="sub-field-group">
                    <label className="sub-field-label">
                        <i className="pi pi-calendar-times sub-field-label-icon" />
                        Expiry Date &amp; Time
                    </label>
                    <Calendar
                        value={form.expiry_date}
                        onChange={(e) => update('expiry_date', e.value)}
                        showTime
                        hourFormat="24"
                        showIcon
                        showButtonBar
                        dateFormat="dd/mm/yy"
                        placeholder="Never expires"
                        minDate={new Date()}
                        className={`w-full${errors.expiry_date ? ' p-invalid' : ''}`}
                    />
                    {errors.expiry_date && (
                        <span className="sub-error-msg">
                            <i className="pi pi-exclamation-circle" /> {errors.expiry_date}
                        </span>
                    )}
                </div>

                {/* Status */}
                <div className="sub-field-group">
                    <label className="sub-field-label">
                        <i className="pi pi-circle sub-field-label-icon" />
                        Status
                    </label>
                    <Dropdown
                        value={form.status || 'Active'}
                        options={STATUS_OPTIONS}
                        onChange={(e) => update('status', e.value)}
                        className="w-full"
                    />
                </div>

                {multiTarget && targetSummary() && (
                    <div className="sub-field-group sub-field-full">
                        <span className="nf-dialog-summary">
                            <i className="pi pi-info-circle" /> {targetSummary()}
                        </span>
                    </div>
                )}

            </div>
        </Dialog>
    );
};

export default NotificationsDialog;
